import React from 'react';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { cn } from '@frontend/shadcn/lib/utils';
import { MajorAudienceDemographicHighlightTab } from './components/major-audience-demographic-highlight-tab';
import { MajorAudienceDemographicsIcon } from '@/applications/Social/components/icons/major-audience-demographics-icon';
import type { DemographicsSectionProps } from '@/types/demographics';
import { AgeDistributionView } from './views/age-distribution-view';
import { GenderDistributionView } from './views/gender-distribution-view';
import { CountryDistributionView } from './views/country-distribution-view';
import { CityDistributionView } from './views/city-distribution-view';

interface DemographicsTabProps {
  isActive: boolean;
  onClick: () => void;
  heading: string;
  info: React.ReactNode;
  className?: string;
  layout?: 'side-by-side' | 'stacked';
}

const DemographicsTab: React.FC<DemographicsTabProps> = ({
  isActive,
  onClick,
  heading,
  info,
  className,
  layout = 'side-by-side',
}) => (
  <MajorAudienceDemographicHighlightTab
    headingText={heading}
    infoText={info}
    isActive={isActive}
    onClick={onClick}
    className={cn(
      layout === 'stacked' ? 'flex-1 min-w-0' : '',
      className
    )}
  />
);

/**
 * DemographicsSection - Generic demographics display component
 *
 * @param layout - Controls the arrangement of tabs and graph:
 *   - 'side-by-side' (default): Tabs on left (1/3), graph on right (2/3) on desktop; stacked on mobile
 *   - 'stacked': Tabs on top horizontally, graph below on all screen sizes
 */
export const DemographicsSection: React.FC<DemographicsSectionProps> = ({
  activeDemographicTab,
  setActiveDemographicTab,
  isLoading,
  summary,
  ageDistributionData,
  genderDistributionData,
  topCountriesData,
  topCitiesData,
  layout = 'side-by-side',
}) => {
  const processedGenderInfo = (summary.gender && summary.gender !== '--')
    ? <span className="capitalize">{summary.gender}</span>
    : '--';

  // Layout classes for different configurations
  const layoutClasses = {
    'side-by-side': {
      container: 'grid grid-cols-1 md:grid-cols-3 gap-4 mt-2',
      tabs: 'md:col-span-1 flex flex-col items-start gap-[2px] self-stretch',
      graph: 'md:col-span-2 rounded-lg min-h-[250px] flex flex-col items-start justify-start',
    },
    'stacked': {
      container: 'flex flex-col gap-4 mt-2',
      tabs: 'flex flex-row flex-wrap items-start gap-2 w-full',
      graph: 'rounded-lg min-h-[250px] flex flex-col items-start justify-start',
    },
  };

  const currentLayout = layoutClasses[layout];

  if (isLoading) {
    return (
      <>
        <div className="flex items-center gap-2 mb-3">
          <Skeleton className="w-6 h-6 rounded-lg" />
          <Skeleton className="h-5 w-52" />
        </div>
        <div className={currentLayout.container}>
          <div className={currentLayout.tabs}>
            {[...Array(4)].map((_, i) => (
              <div key={i} className={layout === 'stacked' ? 'flex-shrink-0' : 'mb-2 w-full'}>
                <Skeleton className="h-8 w-full mb-1" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ))}
          </div>
          <div className={`${currentLayout.graph} border bg-muted/40 flex items-center justify-center`}>
            <Skeleton className="h-40 w-40 rounded-full" />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <h3 className="text-md font-semibold mb-3 flex items-center gap-2">
        <div className="w-6 h-6 p-1 rounded-lg bg-teal-4 flex items-center justify-center">
          <MajorAudienceDemographicsIcon />
        </div>
        <span>Major Audience Demographics</span>
      </h3>
      <div className={currentLayout.container}>
        <div className={currentLayout.tabs}>
          <DemographicsTab
            heading="Age Bucket"
            info={summary.ageBucket ?? '--'}
            isActive={activeDemographicTab === 'age'}
            onClick={() => setActiveDemographicTab('age')}
            className={layout === 'stacked' ? 'flex-shrink-0' : 'mb-2'}
            layout={layout}
          />
          <DemographicsTab
            heading="Gender"
            info={processedGenderInfo}
            isActive={activeDemographicTab === 'gender'}
            onClick={() => setActiveDemographicTab('gender')}
            className={layout === 'stacked' ? 'flex-shrink-0' : 'mb-2'}
            layout={layout}
          />
          <DemographicsTab
            heading="Country"
            info={summary.country ?? '--'}
            isActive={activeDemographicTab === 'country'}
            onClick={() => setActiveDemographicTab('country')}
            className={layout === 'stacked' ? 'flex-shrink-0' : 'mb-2'}
            layout={layout}
          />
          <DemographicsTab
            heading="City"
            info={summary.city ?? '--'}
            isActive={activeDemographicTab === 'city'}
            onClick={() => setActiveDemographicTab('city')}
            className={layout === 'stacked' ? 'flex-shrink-0' : 'mb-2'}
            layout={layout}
          />
        </div>
        <div className={currentLayout.graph}>
          {activeDemographicTab === 'age' && <AgeDistributionView ageDistributionData={ageDistributionData} />}
          {activeDemographicTab === 'gender' && (
            <GenderDistributionView genderDistributionData={genderDistributionData} />
          )}
          {activeDemographicTab === 'country' && (
            <CountryDistributionView topCountriesData={topCountriesData} />
          )}
          {activeDemographicTab === 'city' && (
            <CityDistributionView topCitiesData={topCitiesData} />
          )}
        </div>
      </div>
    </>
  );
};

DemographicsSection.displayName = 'DemographicsSection';
